/*!
 * EMA Gift Milestones
 * ---------------------------------------------------------------------------
 * Keeps the free-gift lines of the cart in sync with the spend milestones that
 * are configured in the theme editor (Cart drawer section).
 *
 *  - Reads its configuration from <script id="EmaGiftConfig"> (no hardcoded products/prices).
 *  - Gift lines are cart lines that carry the hidden line-item property `_ema_gift` = "m1" | "m2" | "m3".
 *    Lines WITHOUT that property (anything the customer added) are never added to, changed or removed.
 *  - Listens to the theme's own events (`cart:change`, `cart:refresh`, `pageshow`) and converges the
 *    cart towards the desired state using the Shopify AJAX Cart API (idempotent, max 4 passes).
 *  - Re-renders the drawer through the theme's own `cart:change` event (with Section Rendering API
 *    HTML), so the widget, totals, line items, note and discounts all stay in sync.
 *  - Loop protection: events dispatched by this script are ignored by this script; a single
 *    in-flight run with one coalesced re-run; failed gifts are remembered for the session.
 */
(function () {
  'use strict';

  if (window.__emaGiftMilestones) return;

  var TAG = '_ema_gift';
  var FAIL_KEY = 'ema_gm_fail_v1';
  var FAIL_TTL = 15 * 1000;
  var MAX_PASSES = 4;
  var CHECKOUT_WAIT = 4000; // max time (ms) we wait for the gift sync before going to checkout anyway
  var CHECKOUT_WATCHDOG = 1500; // ms after submitting: if the page is still here, navigate by URL
  // TEMPORARY test logging. Set to false (or delete the log() calls) for the final version.
  var DEBUG = false;
  var shopifyGlobal = window.Shopify;
  var root = (shopifyGlobal && shopifyGlobal.routes && shopifyGlobal.routes.root) || '/';

  function log() {
    if (!DEBUG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[ema-gift-milestones]');
    console.log.apply(console, args);
  }

  var suppress = 0; // > 0 while this script dispatches cart events
  var current = null; // in-flight sync promise
  var again = false; // another event arrived while syncing
  var lastPct = null;
  var lastUnlocked = null;
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* helpers                                                              */
  /* ------------------------------------------------------------------ */

  function readConfig() {
    try {
      var el = document.getElementById('EmaGiftConfig');
      if (!el) return null;
      var cfg = JSON.parse(el.textContent);
      cfg.milestones = (cfg.milestones || [])
        .filter(function (m) { return m && m.amount > 0; })
        .sort(function (a, b) { return a.amount - b.amount; });
      return cfg;
    } catch (e) {
      return null;
    }
  }

  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[TAG]);
  }

  function request(path, options) {
    options = options || {};
    options.cache = 'no-store';
    options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    return fetch(root + path, options).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok) {
          var err = new Error(body.description || body.message || 'HTTP ' + res.status);
          err.status = res.status;
          throw err;
        }
        return body;
      });
    });
  }

  function post(path, data) {
    return request(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  }

  function getCart() {
    return request('cart.js');
  }

  /* failures are remembered per variant (session) so we never retry in a loop */
  function readFails() {
    try { return JSON.parse(sessionStorage.getItem(FAIL_KEY) || '{}'); } catch (e) { return {}; }
  }

  function markFail(variantId, spend, reason) {
    var fails = readFails();
    fails[variantId] = { t: Date.now(), spend: spend, reason: reason };
    try { sessionStorage.setItem(FAIL_KEY, JSON.stringify(fails)); } catch (e) { /* storage blocked */ }
    memoryFails[variantId] = fails[variantId];
  }

  var memoryFails = {};

  function isBlocked(variantId, spend) {
    var f = readFails()[variantId] || memoryFails[variantId];
    if (!f) return false;
    // retry only once the customer changes the cart (different spend) or the TTL has passed
    return Date.now() - f.t < FAIL_TTL && f.spend === spend;
  }

  function setBusy(on) {
    document.querySelectorAll('[data-ema-gm]').forEach(function (el) {
      el.classList.toggle('is-syncing', on);
      el.setAttribute('aria-busy', on ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------------------ */
  /* planning                                                             */
  /* ------------------------------------------------------------------ */

  function computeSpend(cfg, cart) {
    var spend = 0;
    cart.items.forEach(function (item) {
      if (isGiftLine(item) || item.gift_card) return;
      if (cfg.giftWrapVariantId && item.variant_id === cfg.giftWrapVariantId) return;
      spend += cfg.basis === 'before' ? item.original_line_price : item.final_line_price;
    });
    return Math.max(spend, 0);
  }

  /**
   * Pure function: given config + cart, returns what has to change.
   *   removes: line keys to delete (stale / duplicate / wrong / not-free gift lines)
   *   fixes:   line keys whose quantity must be reset to 1
   *   adds:    items to add ({id, quantity, properties})
   */
  function buildPlan(cfg, cart) {
    var spend = computeSpend(cfg, cart);
    var hasPaidLines = cart.items.some(function (i) { return !isGiftLine(i); });

    var wanted = [];
    if (cfg.realGifts && hasPaidLines) {
      wanted = cfg.milestones.filter(function (m) {
        return m.variantIds && m.variantIds.length && spend >= m.amount;
      });
      if (cfg.mode === 'highest' && wanted.length > 1) wanted = [wanted[wanted.length - 1]];
    }

    // wanted gift lines: one per (milestone, gift variant)
    var wantedLines = {};
    wanted.forEach(function (m) {
      (m.variantIds || []).forEach(function (vid) {
        if (!isBlocked(vid, spend)) wantedLines['m' + m.index + ':' + vid] = { tag: 'm' + m.index, variantId: vid };
      });
    });

    var removes = [];
    var fixes = [];
    var have = {};

    cart.items.forEach(function (item) {
      var tag = item.properties && item.properties[TAG];
      if (!tag) return; // customer line: never touched

      var id = tag + ':' + item.variant_id;
      if (!wantedLines[id] || have[id]) {
        removes.push(item.key);
        return;
      }
      // Safety guard: a gift that is still charged means the 100% discount is not applying.
      if (cfg.guard && item.final_line_price > 0) {
        markFail(item.variant_id, spend, 'not-free');
        removes.push(item.key);
        return;
      }
      have[id] = true;
      if (item.quantity !== 1) fixes.push(item.key);
    });

    var adds = Object.keys(wantedLines)
      .filter(function (id) { return !have[id] && !isBlocked(wantedLines[id].variantId, spend); })
      .map(function (id) {
        var props = {};
        props[TAG] = wantedLines[id].tag;
        return { id: wantedLines[id].variantId, quantity: 1, properties: props, _spend: spend };
      });

    return { spend: spend, removes: removes, fixes: fixes, adds: adds };
  }

  function planIsEmpty(plan) {
    return !plan.removes.length && !plan.fixes.length && !plan.adds.length;
  }

  /* ------------------------------------------------------------------ */
  /* applying  (fast path: 1 request for removals, 2 when gifts are added) */
  /* ------------------------------------------------------------------ */

  function sectionIds() {
    var ids = [];
    suppress++;
    try {
      document.dispatchEvent(new CustomEvent('cart:push:section.id', { bubbles: true, detail: { sections: ids } }));
    } finally {
      suppress--;
    }
    return ids.filter(function (id, i) { return id && ids.indexOf(id) === i; });
  }

  function addOne(item) {
    return post('cart/add.js', { id: item.id, quantity: 1, properties: item.properties }).catch(function (err) {
      if (err.status === 422 || err.status === 404) {
        // out of stock / unavailable / quantity limit: remember and carry on without it
        markFail(item.id, item._spend, 'add-failed');
        document.dispatchEvent(new CustomEvent('ema:gift:error', { detail: { variantId: item.id, message: err.message } }));
        return null;
      }
      throw err;
    });
  }

  function addGifts(adds, oneByOne) {
    if (!adds.length) return Promise.resolve();
    if (oneByOne) {
      return adds.reduce(function (chain, item) {
        return chain.then(function () { return addOne(item); });
      }, Promise.resolve());
    }
    var items = adds.map(function (a) { return { id: a.id, quantity: 1, properties: a.properties }; });
    // all gifts in ONE request; if one of them is rejected, retry them one by one
    return post('cart/add.js', { items: items }).catch(function (err) {
      if (err.status !== 422 && err.status !== 404) throw err;
      return adds.reduce(function (chain, item) {
        return chain.then(function () { return addOne(item); });
      }, Promise.resolve());
    });
  }

  function changeSequential(plan) {
    var chain = Promise.resolve();
    plan.removes.forEach(function (key) {
      chain = chain.then(function () { return post('cart/change.js', { id: key, quantity: 0 }); });
    });
    plan.fixes.forEach(function (key) {
      chain = chain.then(function () { return post('cart/change.js', { id: key, quantity: 1 }); });
    });
    return chain;
  }

  function withSections(body, ids) {
    if (ids.length) body.sections = ids.join(',');
    return body;
  }

  /* Fast path: only gifts to add => ONE request (add.js also renders the sections). The resulting
     cart is built from the cart we already know + the added lines; the guard re-checks their price. */
  function fastAdd(plan, cart, ids) {
    var items = plan.adds.map(function (a) { return { id: a.id, quantity: 1, properties: a.properties }; });
    return post('cart/add.js', withSections({ items: items }, ids)).then(function (res) {
      if (!res || !Array.isArray(res.items) || !res.sections) return null; // unexpected shape: use slow path
      var next = JSON.parse(JSON.stringify(cart));
      res.items.forEach(function (line) {
        next.items.push(line);
        next.item_count = (next.item_count || 0) + (line.quantity || 0);
        next.total_price = (next.total_price || 0) + (line.final_line_price || 0);
      });
      next.sections = res.sections;
      return next;
    }, function (err) {
      if (err.status !== 422 && err.status !== 404) throw err;
      return null; // one gift rejected: slow path adds them one by one
    });
  }

  /* Returns the final cart (with rendered `sections` when available). */
  function applyPlan(plan, ids, cart) {
    if (plan.adds.length && !plan.removes.length && !plan.fixes.length && cart && ids.length) {
      return fastAdd(plan, cart, ids).then(function (next) {
        if (next) return next;
        return applyPlanSlow(plan, ids, true);
      });
    }
    return applyPlanSlow(plan, ids, false);
  }

  function applyPlanSlow(plan, ids, oneByOne) {
    return addGifts(plan.adds, oneByOne).then(function () {
      var updates = {};
      plan.removes.forEach(function (k) { updates[k] = 0; });
      plan.fixes.forEach(function (k) { updates[k] = 1; });
      // update.js always returns the full cart (+ rendered sections) => no extra GETs needed
      return post('cart/update.js', withSections({ updates: updates }, ids)).then(function (cart) {
        var stale = cart.items.some(function (i) { return plan.removes.indexOf(i.key) > -1; });
        if (!stale) return cart;
        // line keys were not honoured: fall back to change.js, one line at a time
        return changeSequential(plan).then(function () { return getCart(); });
      }, function (err) {
        if (!(err.status >= 400 && err.status < 500)) throw err;
        return changeSequential(plan).then(function () { return getCart(); });
      });
    });
  }

  /* hand the final cart state to the theme so drawer / counters / cart page re-render */
  function publish(cart, ids) {
    var hasAll = cart.sections && ids.every(function (id) { return typeof cart.sections[id] === 'string'; });
    var ready = hasAll
      ? Promise.resolve(cart.sections)
      : (ids.length
          ? request('?sections=' + encodeURIComponent(ids.join(','))).catch(function () { return null; })
          : Promise.resolve(null));

    return ready.then(function (sections) {
      suppress++;
      try {
        if (sections && ids.every(function (id) { return typeof sections[id] === 'string'; })) {
          cart.sections = sections;
          document.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'ema-gift:sync', cart: cart }
          }));
        } else {
          // could not get rendered sections: let the theme re-fetch everything itself
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      } finally {
        suppress--;
      }
    });
  }

  /* ---- instant feedback while the requests are in flight ---- */

  function isArabicPage() {
    return /^ar/i.test(document.documentElement.getAttribute('lang') || '');
  }

  function giftMeta(cfg, variantId) {
    var found = null;
    cfg.milestones.forEach(function (m) {
      (m.gifts || []).forEach(function (g) { if (g.id === variantId) found = g; });
    });
    return found;
  }

  function esc(str) {
    return String(str || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function showOptimistic(plan, cart, cfg) {
    try {
      var rows = document.querySelectorAll('#CartDrawer .hdt-mini-cart__items > .hdt-cart-item');
      plan.removes.forEach(function (key) {
        var idx = cart.items.findIndex(function (i) { return i.key === key; });
        var row = idx > -1 && rows[idx];
        if (row && row.classList.contains('is-ema-gift')) row.classList.add('ema-gm__leaving');
      });
      var list = document.querySelector('#CartDrawer .hdt-mini-cart__items');
      if (!list) return;
      var ar = isArabicPage();
      plan.adds.forEach(function (add) {
        var meta = giftMeta(cfg, add.id) || {};
        var el = document.createElement('div');
        el.className = 'hdt-mini-cart__item hdt-cart-item hdt-flex hdt-relative hdt-oh hdt-line-item is-ema-gift ema-gm__ghost';
        el.innerHTML =
          '<div class="hdt-mini-cart__image"><span class="hdt-mini-cart__img hdt-block hdt-relative hdt-oh hdt-ratio" style="--aspect-ratioapt:1">' +
          (meta.image ? '<img src="' + esc(meta.image) + '" alt="">' : '') + '</span></div>' +
          '<div class="hdt-mini-cart__info hdt-s-text hdt-relative hdt-oh">' +
          '<div class="ema-cart__item-top"><span class="hdt-mini-cart__title hdt-truncate hdt-block hdt-s-text">' + esc(meta.title || '') + '</span></div>' +
          '<div class="hdt-mini-cart__btns hdt-flex hdt-align-center hdt-justify-between"><div class="hdt-flex hdt-align-center">' +
          '<span class="ema-gm__line-badge">🎁 ' + (ar ? 'هدية مجانية' : 'Free gift') + '</span><span class="ema-gm__line-qty">× 1</span></div></div></div>';
        list.appendChild(el);
      });
    } catch (e) { /* cosmetic only */ }
  }

  function clearOptimistic() {
    document.querySelectorAll('.ema-gm__pending, .ema-gm__ghost').forEach(function (el) { el.remove(); });
    document.querySelectorAll('.ema-gm__leaving').forEach(function (el) { el.classList.remove('ema-gm__leaving'); });
  }

  function runOnce(startCart) {
    var cfg = readConfig();
    if (!cfg) return Promise.resolve();

    var idsCache = null;
    function ids() { return idsCache || (idsCache = sectionIds()); }
    var mutated = false;

    function pass(n, cart) {
      var plan = buildPlan(cfg, cart);
      if (planIsEmpty(plan) || n >= MAX_PASSES) return Promise.resolve(cart);
      mutated = true;
      setBusy(true);
      showOptimistic(plan, cart, cfg);
      return applyPlan(plan, ids(), cart).then(function (next) { return pass(n + 1, next); });
    }

    // the theme already gives us the fresh cart in its event: no extra round trip
    return (startCart ? Promise.resolve(startCart) : getCart())
      .then(function (cart) { return pass(0, cart); })
      .then(function (cart) {
        if (!mutated) return null;
        return publish(cart, ids());
      })
      .catch(function (err) {
        if (window.console) console.warn('[ema-gift-milestones] sync failed:', err);
        log('milestone sync failed', err && err.message);
        if (mutated) {
          // some requests may have gone through: show the real cart, but never retry automatically
          return getCart().then(function (c) { return publish(c, ids()); }).catch(function () { /* give up silently */ });
        }
      });
  }

  function sync(startCart) {
    if (current) {
      again = true;
      return current;
    }
    function done() {
      current = null;
      setBusy(false);
      clearOptimistic();
    }
    current = (function loop(cart) {
      again = false;
      return runOnce(cart).then(function () {
        if (again) return loop(null); // newer events arrived: re-evaluate against a fresh cart
      });
    })(startCart).then(done, done);
    return current;
  }

  function schedule(startCart) {
    sync(startCart);
  }

  function flush() {
    return sync(null);
  }

  /* ------------------------------------------------------------------ */
  /* progress animation (widget is re-rendered server side on each change) */
  /* ------------------------------------------------------------------ */

  function hydrate() {
    var widget = document.querySelector('[data-ema-gm]');
    if (!widget) return;
    var fill = widget.querySelector('.ema-gm__fill');
    var target = parseFloat(widget.getAttribute('data-progress')) || 0;

    var unlocked = {};
    widget.querySelectorAll('.ema-gm__ms.is-unlocked').forEach(function (el) {
      unlocked[el.getAttribute('data-gm-index')] = el;
    });

    if (fill && !reducedMotion && lastPct !== null && lastPct !== target) {
      fill.style.transition = 'none';
      fill.style.setProperty('--gm-p', lastPct);
      void fill.offsetWidth; // commit the start value
      fill.style.transition = '';
      requestAnimationFrame(function () { fill.style.setProperty('--gm-p', target); });
    }

    if (lastUnlocked && !reducedMotion) {
      Object.keys(unlocked).forEach(function (idx) {
        if (!lastUnlocked[idx]) unlocked[idx].classList.add('is-pop');
      });
    }

    lastPct = target;
    lastUnlocked = {};
    Object.keys(unlocked).forEach(function (idx) { lastUnlocked[idx] = true; });
  }

  /* ------------------------------------------------------------------ */
  /* checkout: make sure gifts are correct before leaving the store       */
  /* ------------------------------------------------------------------ */

  var checkoutBusy = false; // true from the first Checkout click until we leave the page
  var bypassSubmit = false; // true only while WE re-submit the form (so onSubmit lets it through)

  function checkoutUrl() {
    return root + 'checkout';
  }

  function goToCheckoutByUrl(reason) {
    log('redirecting to checkout (url)', reason);
    window.location.href = checkoutUrl();
  }

  /* The theme re-renders the cart page after a sync, which REPLACES the <form> and the Checkout
     button that were captured at click time. Submitting those stale (detached) nodes throws or does
     nothing, so the customer would stay on /cart. Always look the elements up again. */
  function freshCheckoutParts(formId, oldForm) {
    var form = (formId && document.getElementById(formId)) || (oldForm && oldForm.isConnected ? oldForm : null);
    if (!form || !form.isConnected) return null;
    var btn = null;
    var candidates = document.querySelectorAll('button[name="checkout"], input[name="checkout"]');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].form === form) { btn = candidates[i]; break; }
    }
    return { form: form, btn: btn };
  }

  function submitToCheckout(formId, oldForm) {
    var parts = freshCheckoutParts(formId, oldForm);
    if (!parts) return goToCheckoutByUrl('form not found after sync');

    var navigating = false;
    function onLeave() { navigating = true; }
    window.addEventListener('pagehide', onLeave, { once: true });

    try {
      bypassSubmit = true;
      log('redirecting to checkout (form submit)');
      if (parts.btn && typeof parts.form.requestSubmit === 'function') parts.form.requestSubmit(parts.btn);
      else if (typeof parts.form.requestSubmit === 'function') parts.form.requestSubmit();
      else parts.form.submit();
    } catch (err) {
      bypassSubmit = false;
      return goToCheckoutByUrl('submit threw: ' + (err && err.message));
    } finally {
      // the submit event is dispatched synchronously, so the flag can be cleared right away
      bypassSubmit = false;
    }

    // Watchdog: if some other script cancelled the submit, do not leave the customer stuck.
    setTimeout(function () {
      if (!navigating && document.visibilityState !== 'hidden') goToCheckoutByUrl('watchdog');
    }, CHECKOUT_WATCHDOG);
  }

  function onSubmit(event) {
    if (bypassSubmit) return; // our own re-submit: let the theme / browser handle it
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;

    if (checkoutBusy) { // double click / double Enter: swallow it, the first one is already running
      event.preventDefault();
      event.stopPropagation();
      log('checkout click ignored (already in progress)');
      return;
    }
    log('checkout clicked');

    // Let the browser show its own message for an unchecked required box ("I agree..."), etc.
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

    // No gift config on this page => nothing to sync, keep the native behaviour.
    if (!readConfig()) return;

    event.preventDefault();
    event.stopPropagation();
    checkoutBusy = true;

    var formId = form.id;
    var finished = false;
    function go(reason) {
      if (finished) return;
      finished = true;
      log('sync step finished, reason:', reason);
      try {
        submitToCheckout(formId, form);
      } catch (err) {
        goToCheckoutByUrl('go() threw: ' + (err && err.message));
      }
    }

    var timer = setTimeout(function () {
      log('milestone sync timed out after ' + CHECKOUT_WAIT + 'ms');
      go('timeout');
    }, CHECKOUT_WAIT);

    var synced;
    try {
      log('milestone sync started');
      synced = Promise.resolve(flush());
    } catch (err) {
      synced = Promise.reject(err);
    }
    synced.then(
      function () { log('milestone sync completed'); clearTimeout(timer); go('synced'); },
      function (err) { log('milestone sync failed', err && err.message); clearTimeout(timer); go('sync error'); }
    );
  }

  /* ------------------------------------------------------------------ */
  /* wiring                                                               */
  /* ------------------------------------------------------------------ */

  function onCartEvent(e) {
    if (suppress > 0) return; // our own event: never re-trigger ourselves
    var cart = e && e.type === 'cart:change' && e.detail && e.detail.cart;
    schedule(cart && Array.isArray(cart.items) ? cart : null);
  }

  window.__emaGiftMilestones = { sync: flush, buildPlan: buildPlan, computeSpend: computeSpend };

  document.addEventListener('cart:change', onCartEvent);
  document.addEventListener('cart:refresh', onCartEvent);
  document.addEventListener('cart:drawer:render', hydrate);
  document.addEventListener('submit', onSubmit, true);
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) { checkoutBusy = false; schedule(); }
  });

  function init() {
    hydrate();
    schedule(); // initial convergence (handles page refresh / returning customers)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
